import { createI18n } from 'vue-i18n'

import { DEFAULT_LANGUAGE } from '@/config/lang'
import type { Language } from '@/config/lang'
import { options } from '@/plugins/PrimeVue'
import { PRIME_LOCALES } from '@/plugins/PrimeVue/locale'
import { getLanguage, setLanguage } from '@/utils/language'
import en from '@/i18n/locales/en'
import ru from '@/i18n/locales/ru'
import uz from '@/i18n/locales/uz'

/**
 * Переводы интерфейса. Язык хранится там же, где и раньше, — в cookie
 * `i18n_redirected`: её читает прокси, чтобы подставить `Language` в запросы
 * к бэкенду. То есть тексты меню и статусов приходят на том же языке, что и
 * интерфейс, и синхронизировать их отдельно не нужно.
 *
 * `legacy: false` — Composition API: в компонентах это `const { t } = useI18n()`.
 */
/**
 * Русские числительные: 1 товар, 2 товара, 5 товаров. Формы в словаре идут
 * в порядке «один | несколько | много», а vue-i18n без своего правила берёт
 * английское «один/остальные» и пишет «2 товар».
 */
function russianPlural(choice: number): number {
  const hundreds = Math.abs(choice) % 100
  const units = hundreds % 10

  /* 11–14 — всегда «много», иначе 11 попало бы в форму для «один» */
  if (hundreds > 10 && hundreds < 20) return 2
  if (units === 1) return 0
  if (units >= 2 && units <= 4) return 1
  return 2
}

export const i18n = createI18n({
  legacy: false,
  pluralRules: { ru: russianPlural },
  locale: getLanguage(),
  fallbackLocale: DEFAULT_LANGUAGE,
  messages: { ru, uz, en },
})

/**
 * Перевод вне компонента: сторы, композаблы, утилиты. Внутри компонентов так
 * не делать — там `useI18n()`, иначе строка не обновится при смене языка.
 * Здесь это безопасно: такие тексты создаются в момент вызова (тосты, ошибки).
 */
export const t = i18n.global.t

/**
 * Сменить язык: и в интерфейсе, и в cookie для бэкенда. Пользоваться только
 * этим — `setLanguage` в утилитах трогает cookie и ничего не знает про тексты.
 */
/**
 * Забыть выбор языка. Интерфейс возвращается к языку по умолчанию, а cookie
 * стирается — она же служит отметкой «клиент язык выбирал», и без неё
 * онбординг снова начнётся с экрана выбора.
 */
export function resetLocale(): void {
  setLanguage('')
  i18n.global.locale.value = DEFAULT_LANGUAGE
  Object.assign(options.locale ?? {}, PRIME_LOCALES[DEFAULT_LANGUAGE])
}

export function setLocale(value: Language): void {
  setLanguage(value)
  i18n.global.locale.value = value

  /*
   * У PrimeVue свой словарь (месяцы, дни недели, «Сегодня»). Объект реактивный
   * и живёт в конфиге плагина — мутируем его на месте, иначе календарь остался
   * бы на языке, который стоял при запуске.
   */
  Object.assign(options.locale ?? {}, PRIME_LOCALES[value])
}
